import Add from '@mui/icons-material/Add';
import { paperClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  LoadingSpinner,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { FuelPriceIndex as FuelPriceIndexModel } from '~hooks/useFuelPriceIndices/models';
import { useFuelPriceIndices } from '~hooks/useFuelPriceIndices/useFuelPriceIndices';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import FuelPriceIndexForm, {
  FuelPriceIndexFormHandler,
} from './components/FuelPriceIndexForm';
import { FuelPriceIndexGroup } from './components/FuelPriceIndexGroup';
import FuelPriceIndexPriceTable from './components/FuelPriceIndexPriceTable';
import { FuelPriceIndexRow } from './components/FuelPriceIndexRow';

interface FuelPriceIndexDialogState {
  isOpen: boolean;
  isSaving: boolean;
  mode: 'create' | 'edit';
  editingIndex?: FuelPriceIndexModel;
}

interface DeleteDialogState {
  isOpen: boolean;
  isDeleting: boolean;
  indexToDelete?: FuelPriceIndexModel;
}

const FuelPriceIndex = observer(function FuelPriceIndex() {
  const theme = useTheme();
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const {
    createFuelPriceIndex,
    getCompanyFuelPriceIndices,
    updateFuelPriceIndex,
    deleteFuelPriceIndex,
  } = useFuelPriceIndices();
  const [fuelPriceIndices, setFuelPriceIndices] = useState<FuelPriceIndexModel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState<string | undefined>();
  const formRef = useRef<FuelPriceIndexFormHandler>(null);

  const [dialogState, setDialogState] = useState<FuelPriceIndexDialogState>({
    isOpen: false,
    isSaving: false,
    mode: 'create',
  });

  const [deleteDialogState, setDeleteDialogState] = useState<DeleteDialogState>({
    isOpen: false,
    isDeleting: false,
  });

  const loadFuelPriceIndices = async () => {
    try {
      setIsLoading(true);
      const { data } = await getCompanyFuelPriceIndices({
        limit: MAX_API_PAGE_SIZE,
      });
      setFuelPriceIndices(data);
      setSelectedIndex(data.find((index) => !index.system)?.id);
    } catch {
      // Silently fail - Toaster already shown in the hook
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setSelectedIndex(undefined);
    loadFuelPriceIndices();
  }, [companyId]);

  const openCreateDialog = useCallback(() => {
    setDialogState({ isOpen: true, isSaving: false, mode: 'create' });
  }, []);

  const openEditDialog = useCallback((index: FuelPriceIndexModel) => {
    setDialogState({ isOpen: true, isSaving: false, mode: 'edit', editingIndex: index });
  }, []);

  const closeDialog = useCallback(() => {
    setDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleCreateFuelPriceIndex = useCallback(async () => {
    const formData = await formRef.current?.submit();
    if (!formData) {
      return;
    }

    try {
      setDialogState((state) => ({ ...state, isSaving: true }));

      const newIndex = await createFuelPriceIndex({
        name: formData.name,
      });

      setFuelPriceIndices((indices) => [...indices, newIndex]);
      setSelectedIndex(newIndex.id);

      toasterStore.push(
        alert(t('administration.fuel_price_index.created_message'), AlertTypes.success),
      );

      closeDialog();
    } catch (error) {
      console.error('Failed to create fuel price index:', error);
    } finally {
      setDialogState((state) => ({
        ...state,
        isSaving: false,
      }));
    }
  }, [companyId, createFuelPriceIndex, toasterStore, closeDialog]);

  const handleUpdateFuelPriceIndex = useCallback(async () => {
    const formData = await formRef.current?.submit();
    if (!formData || !dialogState.editingIndex) {
      return;
    }

    try {
      setDialogState((state) => ({ ...state, isSaving: true }));

      const updatedIndex = await updateFuelPriceIndex({
        id: dialogState.editingIndex.id,
        name: formData.name,
      });

      setFuelPriceIndices((indices) =>
        indices.map((index) => (index.id === updatedIndex.id ? updatedIndex : index)),
      );

      toasterStore.push(
        alert(t('administration.fuel_price_index.updated_message'), AlertTypes.success),
      );

      closeDialog();
    } catch (error) {
      console.error('Failed to update fuel price index:', error);
    } finally {
      setDialogState((state) => ({
        ...state,
        isSaving: false,
      }));
    }
  }, [updateFuelPriceIndex, toasterStore, closeDialog, dialogState.editingIndex]);

  const handleEdit = useCallback(
    (index: FuelPriceIndexModel, event: React.MouseEvent) => {
      event.stopPropagation();
      openEditDialog(index);
    },
    [openEditDialog],
  );

  const openDeleteDialog = useCallback((index: FuelPriceIndexModel) => {
    setDeleteDialogState({
      isOpen: true,
      isDeleting: false,
      indexToDelete: index,
    });
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setDeleteDialogState((state) => ({ ...state, isOpen: false }));
  }, []);

  const handleDelete = useCallback(
    (index: FuelPriceIndexModel, event: React.MouseEvent) => {
      event.stopPropagation();
      openDeleteDialog(index);
    },
    [openDeleteDialog],
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteDialogState.indexToDelete) {
      return;
    }

    try {
      setDeleteDialogState((state) => ({ ...state, isDeleting: true }));

      await deleteFuelPriceIndex(deleteDialogState.indexToDelete.id);

      setFuelPriceIndices((indices) =>
        indices.filter((index) => index.id !== deleteDialogState.indexToDelete?.id),
      );

      if (selectedIndex === deleteDialogState.indexToDelete.id) {
        setSelectedIndex(undefined);
      }

      toasterStore.push(
        alert(t('administration.fuel_price_index.deleted_message'), AlertTypes.success),
      );

      closeDeleteDialog();
    } catch (error) {
      console.error('Failed to delete fuel price index:', error);
    } finally {
      setDeleteDialogState((state) => ({
        ...state,
        isDeleting: false,
      }));
    }
  }, [
    deleteDialogState.indexToDelete,
    deleteFuelPriceIndex,
    selectedIndex,
    toasterStore,
    closeDeleteDialog,
  ]);

  const handleDialogSubmit = useCallback(() => {
    if (dialogState.mode === 'create') {
      handleCreateFuelPriceIndex();
    } else {
      handleUpdateFuelPriceIndex();
    }
  }, [dialogState.mode, handleCreateFuelPriceIndex, handleUpdateFuelPriceIndex]);

  const systemIndices = fuelPriceIndices.filter((index) => index.system);
  const customIndices = fuelPriceIndices.filter((index) => !index.system);
  const selectedFuelPriceIndex = fuelPriceIndices.find(
    (index) => index.id === selectedIndex,
  );
  const renderRow = (index: FuelPriceIndexModel) => (
    <FuelPriceIndexRow
      key={index.id}
      index={index}
      isSelected={selectedIndex === index.id}
      onSelect={setSelectedIndex}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  );

  return (
    <>
      <Box
        sx={{
          border: `solid 1px ${theme.palette.divider}`,
          display: 'flex',
          // 226px = app bar + SettingsPageHeader + tabs + this panel's top margin, so the
          // panel bottom-aligns to the viewport.
          height: 'calc(100vh - 226px - var(--tread-agent-dock-height, 0px))',
          mb: -4,
          mt: 2,
        }}
      >
        <Box
          sx={{
            borderRight: `solid 1px ${theme.palette.divider}`,
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            height: '100%',
            maxWidth: '280px',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', p: 1.5, flex: 0 }}>
            <SmallButton
              color="brandV2Yellow"
              onClick={openCreateDialog}
              startIcon={<Add />}
            >
              {t('administration.fuel_price_index.add_price_index_cta')}
            </SmallButton>
          </Box>

          <Box
            sx={{
              borderTop: `solid 1px ${theme.palette.divider}`,
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              overflow: 'auto',
            }}
          >
            {isLoading ? (
              <LoadingSpinner isVisible sx={{ mt: 2 }} />
            ) : (
              <>
                {systemIndices.length > 0 && (
                  <FuelPriceIndexGroup
                    title={t('administration.fuel_price_index.default_group_title', {
                      count: systemIndices.length,
                    })}
                    subtitle={t('administration.fuel_price_index.default_group_subtitle')}
                    defaultExpanded={false}
                  >
                    {systemIndices.map(renderRow)}
                  </FuelPriceIndexGroup>
                )}
                {customIndices.length > 0 && (
                  <FuelPriceIndexGroup
                    title={t('administration.fuel_price_index.custom_group_title', {
                      count: customIndices.length,
                    })}
                    defaultExpanded
                  >
                    {customIndices.map(renderRow)}
                  </FuelPriceIndexGroup>
                )}
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: 1.5 }}>
          {isLoading ? (
            <LoadingSpinner isVisible sx={{ mt: 2 }} />
          ) : selectedFuelPriceIndex ? (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="h6">{selectedFuelPriceIndex.name}</Typography>
                {selectedFuelPriceIndex.system && (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('administration.fuel_price_index.system_index_helper')}
                  </Typography>
                )}
              </Box>
              <FuelPriceIndexPriceTable
                key={selectedIndex}
                selectedIndex={selectedIndex}
                editable={!selectedFuelPriceIndex.system}
              />
            </Stack>
          ) : (
            <Typography sx={{ color: 'text.secondary' }}>
              {t('administration.fuel_price_index.select_index')}
            </Typography>
          )}
        </Box>
      </Box>

      <ConfirmationDialog
        confirmButtonProps={{ children: t('actions.delete'), color: 'error' }}
        isBusy={deleteDialogState.isDeleting}
        isOpen={deleteDialogState.isOpen}
        onClose={() => {
          if (!deleteDialogState.isDeleting) {
            closeDeleteDialog();
          }
        }}
        onConfirm={handleConfirmDelete}
        title={t('administration.fuel_price_index.delete_confirm_title')}
      >
        <Typography>
          {t('administration.fuel_price_index.delete_confirm_message')}
        </Typography>
      </ConfirmationDialog>

      <Dialog
        sx={{ [`& .${paperClasses.root}`]: { maxWidth: '500px', overflowY: 'visible' } }}
        isOpen={dialogState.isOpen}
        onClose={() => {
          if (!dialogState.isSaving) {
            closeDialog();
          }
        }}
        preventCloseOnBackdropClick
      >
        <DialogHeader
          onClose={() => {
            if (!dialogState.isSaving) {
              closeDialog();
            }
          }}
        >
          {dialogState.mode === 'create'
            ? t('administration.fuel_price_index.create_form_title')
            : t('administration.fuel_price_index.edit_form_title')}
        </DialogHeader>

        <DialogContent>
          <FuelPriceIndexForm
            ref={formRef}
            initialValues={
              dialogState.mode === 'edit'
                ? { name: dialogState.editingIndex?.name ?? '' }
                : undefined
            }
          />
        </DialogContent>

        <DialogActions>
          <SmallButton
            color="secondary"
            disabled={dialogState.isSaving}
            onClick={closeDialog}
            variant="outlined"
          >
            {t('actions.cancel')}
          </SmallButton>

          <SmallLoadingButton
            color="brandV2Yellow"
            disabled={dialogState.isSaving}
            loading={dialogState.isSaving}
            onClick={handleDialogSubmit}
          >
            {dialogState.mode === 'create' ? t('actions.create') : t('actions.save')}
          </SmallLoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default FuelPriceIndex;
